/**
 * DAGCanvas.tsx
 *
 * Status-aware directed-acyclic-graph canvas for Mission Control.
 *
 * Data flow:
 *   1. Fetch  GET /v1/runs/{runId}/dag-map  → RunDAGMap
 *   2. Pass nodes + edges through a dagre left-to-right layout pass
 *      (positions cached; dagre only reruns when node topology changes)
 *   3. Map to React Flow node/edge objects with per-department colours and
 *      per-status CSS classes (including `glow-blue` / `glow-amber`)
 *   4. Render inside <ReactFlow> with fitView, Background, Controls, MiniMap
 *   5. Open SSE stream  GET /v1/runs/{runId}/events  via a fetch-based reader
 *      (EventSource cannot send custom auth headers).
 *      node.status_changed  → patch matching node in dagMap
 *      approval.requested   → set node status to waiting_for_approval
 *      run.status_changed   → patch dagMap.runStatus
 *      All patches are flushed on the next animation frame to batch
 *      rapid-fire events into a single React render.
 *      On error: exponential back-off (1 s → 2 s → … → 30 s).
 *
 * The custom node (`SkillNode`) is defined outside the component tree so
 * React Flow never sees a new `nodeTypes` reference between renders.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import { Graph, layout } from '@dagrejs/dagre'
import type { GraphLabel, NodeLabel } from '@dagrejs/dagre'
import '@xyflow/react/dist/style.css'

import { api, apiErrorMessage } from '../api/client'
import { useObservabilityStore } from '../store/useObservabilityStore'
import { ReviewSidebar } from './ReviewSidebar'
import { StepDetailsSidebar } from './StepDetailsSidebar'
import './DAGCanvas.css'

// ── Go contract mirrors ───────────────────────────────────────────────────────
// These types are exported so ReviewSidebar can import them without duplication.

export type DAGNodeStatus =
  | 'completed'
  | 'running'
  | 'waiting_for_approval'
  | 'failed'
  | 'pending'

export type GoDAGNode = {
  id: string
  label: string
  skillId?: string
  department?: string
  status: DAGNodeStatus
  stepIndex: number
  stepType: string
  startedAt?: string
  finishedAt?: string
  errorCode?: string
  errorMessage?: string
  /**
   * Structured data the agent proposes to act on.
   * Displayed in the ReviewSidebar's JSON inspector when the node is
   * waiting_for_approval.  Mirrors the Go `proposed_action` JSON field.
   */
  proposedAction?: Record<string, unknown>
  /** The pending approval's UUID, populated by the backend for waiting_for_approval
   *  nodes so the approval can be actioned without a store lookup. Mirrors Go's approvalId. */
  approvalId?: string
}

export type GoDAGEdge = {
  sourceNodeId: string
  targetNodeId: string
  label?: string
}

export type RunDAGMap = {
  runId: string
  tenantId: string
  agentId: string
  runStatus: string
  nodes: GoDAGNode[]
  edges: GoDAGEdge[]
}

// ── SSE event shapes (mirrors Go backend SSE payloads) ────────────────────────

/** SSE connection lifecycle state — drives the header indicator pill. */
type SSEConnectionStatus = 'idle' | 'connecting' | 'live' | 'error' | 'closed'

type NodeStatusChangedEvent = {
  nodeId: string
  nodeStatus: DAGNodeStatus
  /** Go also includes the updated run-level status in every node event. */
  runStatus?: string
  errorCode?: string
  errorMessage?: string
}

type ApprovalRequestedEvent = {
  nodeId: string
}

type RunStatusChangedEvent = {
  runStatus: string
}

// ── Config maps ───────────────────────────────────────────────────────────────

/**
 * Maps a department name (as returned by the Go backend) to its accent colour.
 * The colour drives the department badge background/text and the node border
 * highlight.  All values are CSS hex colours.
 */
const DEPT_ACCENT: Record<string, string> = {
  Research:    '#38bdf8', // sky-400
  HR:          '#60a5fa', // blue-400
  Finance:     '#34d399', // emerald-400
  Recruitment: '#a78bfa', // violet-400
  Legal:       '#f472b6', // pink-400
  Sales:       '#fb923c', // orange-400
  Operations:  '#94a3b8', // slate-400
  Engineering: '#f87171', // red-400
  Product:     '#fbbf24', // amber-400
}
const DEPT_DEFAULT = '#64748b' // slate-500

type StatusConfig = {
  color: string
  label: string
  /** CSS class added to .dag-node — drives the pulsing glow animation. */
  glowClass: string
}

const STATUS_CONFIG: Record<DAGNodeStatus, StatusConfig> = {
  completed:            { color: '#22c55e', label: 'Completed',        glowClass: ''           },
  running:              { color: '#3b82f6', label: 'Running',           glowClass: 'glow-blue'  },
  waiting_for_approval: { color: '#f59e0b', label: 'Awaiting Approval', glowClass: 'glow-amber' },
  failed:               { color: '#ef4444', label: 'Failed',            glowClass: ''           },
  pending:              { color: '#475569', label: 'Pending',           glowClass: ''           },
}

const SSE_STATUS_CONFIG: Record<SSEConnectionStatus, { color: string; label: string; pulse: boolean }> = {
  idle:       { color: '#475569', label: 'SSE',          pulse: false },
  connecting: { color: '#f59e0b', label: 'Connecting…',  pulse: true  },
  live:       { color: '#22c55e', label: 'Live',         pulse: true  },
  error:      { color: '#ef4444', label: 'Retrying',     pulse: false },
  closed:     { color: '#64748b', label: 'Reconnecting', pulse: false },
}

// ── Node dimensions (must match the dagre layout pass) ────────────────────────

const NODE_W = 200
const NODE_H = 86 // approximate rendered height; dagre uses this for spacing

// ── Dagre layout ──────────────────────────────────────────────────────────────

type LayoutedResult<N extends Node, E extends Edge> = { nodes: N[]; edges: E[] }

/**
 * Runs dagre's LR layout over the supplied nodes and edges, writing `position`
 * back into each node.  Does not mutate the input arrays.
 */
function applyDagreLayout<N extends Node, E extends Edge>(
  nodes: N[],
  edges: E[],
): LayoutedResult<N, E> {
  if (nodes.length === 0) return { nodes, edges }

  const g = new Graph<GraphLabel, NodeLabel, Record<string, unknown>>()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: 'LR',
    nodesep: 44,   // vertical gap between nodes in same rank
    ranksep: 88,   // horizontal gap between ranks
    marginx: 24,
    marginy: 24,
  })

  for (const n of nodes) g.setNode(n.id, { width: NODE_W, height: NODE_H })
  for (const e of edges) g.setEdge(e.source, e.target)

  layout(g)

  return {
    nodes: nodes.map((n) => {
      const pos = g.node(n.id)
      return {
        ...n,
        position: { x: (pos.x ?? 0) - NODE_W / 2, y: (pos.y ?? 0) - NODE_H / 2 },
      }
    }),
    edges,
  }
}

// ── React Flow type aliases ───────────────────────────────────────────────────

type SkillNodeData = {
  label: string
  skillId: string
  department: string
  status: DAGNodeStatus
  stepIndex: number
  stepType: string
  errorCode?: string
  errorMessage?: string
}

type SkillNodeType = Node<SkillNodeData, 'skill'>
type RFEdge = Edge<{ edgeLabel?: string }>

// ── Node data helpers ─────────────────────────────────────────────────────────

/** Extract typed `SkillNodeData` from a raw Go DAG node. */
function buildNodeData(n: GoDAGNode): SkillNodeData {
  return {
    label:        n.label,
    skillId:      n.skillId      ?? '',
    department:   n.department   ?? '',
    status:       n.status,
    stepIndex:    n.stepIndex,
    stepType:     n.stepType,
    errorCode:    n.errorCode,
    errorMessage: n.errorMessage,
  }
}

/**
 * Build React Flow edges from a `RunDAGMap`.  Edges whose source node is
 * `running` are animated and coloured blue.
 */
function buildRFEdges(dagMap: RunDAGMap): RFEdge[] {
  const statusByNodeId = new Map(dagMap.nodes.map((n) => [n.id, n.status]))

  return dagMap.edges.map((e, idx) => {
    const srcStatus = statusByNodeId.get(e.sourceNodeId)
    const isActive  = srcStatus === 'running'
    const edgeColor = isActive ? '#3b82f6' : 'rgba(148, 163, 184, 0.32)'

    return {
      id:     `e-${e.sourceNodeId}->${e.targetNodeId}-${idx}`,
      source: e.sourceNodeId,
      target: e.targetNodeId,
      label:  e.label,
      animated: isActive,
      style:    { stroke: edgeColor, strokeWidth: 1.8 },
      markerEnd: {
        type:   MarkerType.ArrowClosed,
        color:  edgeColor,
        width:  14,
        height: 14,
      },
      labelStyle:    { fill: 'rgba(148, 163, 184, 0.75)', fontSize: 10 },
      labelBgStyle:  { fill: 'rgba(8, 13, 20, 0.82)', borderRadius: 4 },
      labelBgPadding: [4, 6] as [number, number],
      data: { edgeLabel: e.label },
    }
  })
}

// ── SSE stream parser ─────────────────────────────────────────────────────────
//
// Native EventSource does not support custom request headers, so we use a
// plain `fetch` and read the response body as a streaming byte source.
// This function parses the W3C SSE wire format and calls `onEvent` for each
// dispatched event.  Resolves when the stream ends; throws on read errors.

async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (eventType: string, data: string) => void,
): Promise<void> {
  const reader  = body.getReader()
  const decoder = new TextDecoder()

  let buffer       = ''
  let currentEvent = 'message'
  let dataLines: string[] = []

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break

      // `stream: true` keeps the decoder's internal state for multi-byte
      // characters that straddle chunk boundaries.
      buffer += decoder.decode(value, { stream: true })

      // Split on LF; pop the last (potentially incomplete) line back into
      // the buffer so we never process a partial SSE field.
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const raw of lines) {
        // Normalise CR+LF line endings.
        const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw

        if (line === '') {
          // Blank line → dispatch the buffered event.
          if (dataLines.length > 0) {
            onEvent(currentEvent, dataLines.join('\n'))
          }
          currentEvent = 'message'
          dataLines    = []
        } else if (line.startsWith('event:')) {
          currentEvent = line.slice(6).trim()
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trimStart())
        }
        // Intentionally ignore `id:`, `retry:`, and comment (`:`) lines.
      }
    }
  } finally {
    reader.releaseLock()
  }
}

// ── Custom node component ─────────────────────────────────────────────────────
// IMPORTANT: defined outside the parent component so React Flow never sees a
// new `nodeTypes` object reference between renders (which would unmount/remount
// every node and reset internal state).

function SkillNode({ data }: NodeProps<SkillNodeType>) {
  const cfg       = STATUS_CONFIG[data.status] ?? STATUS_CONFIG.pending
  const deptColor = DEPT_ACCENT[data.department] ?? DEPT_DEFAULT
  const hasError  = data.status === 'failed' && (data.errorCode ?? data.errorMessage)

  // Status-driven border color class (must keep dag-node for .react-flow__node.selected override)
  const borderColorMap: Record<string, string> = {
    completed:            'border-[rgba(34,197,94,0.30)]',
    running:              'border-[rgba(59,130,246,0.70)]',
    waiting_for_approval: 'border-[rgba(245,158,11,0.70)]',
    failed:               'border-[rgba(239,68,68,0.45)]',
    pending:              'border-[rgba(148,163,184,0.13)]',
  }
  const borderColor = borderColorMap[data.status] ?? borderColorMap.pending

  const cardClasses = [
    'dag-node', // kept for .react-flow__node.selected override in CSS
    borderColor,
    'w-[200px] min-h-[84px] px-[13px] py-[11px] rounded-[13px] border',
    'bg-gradient-to-b from-[rgba(17,26,38,0.96)] to-[rgba(11,17,27,0.98)]',
    'cursor-pointer relative transition-all duration-[180ms] select-none',
    'hover:from-[rgba(22,32,46,0.98)] hover:to-[rgba(14,22,34,0.99)]',
    cfg.glowClass, // 'glow-blue' | 'glow-amber' | ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {/* ── Incoming handle (left / target) ── */}
      <Handle type="target" position={Position.Left} className="dag-handle" />

      <div className={cardClasses}>
        {/* ── Top row: dept badge + step index ── */}
        <div className="flex items-center justify-between gap-[6px] mb-[7px]">
          {data.department ? (
            <span
              className="inline-flex items-center px-[7px] py-0.5 rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border border-transparent leading-[1.5] whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]"
              style={{
                color:       deptColor,
                background:  `${deptColor}16`,
                borderColor: `${deptColor}2e`,
              }}
            >
              {data.department}
            </span>
          ) : (
            <span
              className="inline-flex items-center px-[7px] py-0.5 rounded-full text-[9.5px] font-[800] tracking-[0.1em] uppercase border border-transparent leading-[1.5] whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]"
              style={{ color: DEPT_DEFAULT, background: `${DEPT_DEFAULT}16`, borderColor: `${DEPT_DEFAULT}2e` }}
            >
              {data.stepType}
            </span>
          )}
          <span className="text-[10px] font-bold text-[#4e6480] tabular-nums flex-shrink-0">#{data.stepIndex}</span>
        </div>

        {/* ── Node label ── */}
        <div
          className="text-[13px] font-[800] text-[#dce9f7] leading-[1.3] mb-2 tracking-[-0.01em] overflow-hidden"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {data.label}
        </div>

        {/* ── Status row ── */}
        <div className="flex items-center gap-[6px]">
          <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: cfg.color }} />
          <span className="text-[10px] font-bold tracking-[0.08em] uppercase" style={{ color: cfg.color }}>
            {cfg.label}
          </span>
          {data.skillId && (
            <span
              className="font-mono text-[9.5px] text-[#4a6278] ml-auto overflow-hidden text-ellipsis whitespace-nowrap max-w-[80px]"
              title={data.skillId}
            >
              {data.skillId}
            </span>
          )}
        </div>

        {/* ── Error detail (failed status only) ── */}
        {hasError && (
          <div
            className="mt-[7px] px-2 py-[5px] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.18)] rounded-[6px] text-[10px] text-[#fca5a5] leading-[1.4] overflow-hidden"
            style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
          >
            {data.errorCode ? `[${data.errorCode}] ` : ''}
            {data.errorMessage}
          </div>
        )}
      </div>

      {/* ── Outgoing handle (right / source) ── */}
      <Handle type="source" position={Position.Right} className="dag-handle" />
    </>
  )
}

// nodeTypes MUST be stable (defined at module level)
const nodeTypes = { skill: SkillNode }

// ── FitViewOnChange ───────────────────────────────────────────────────────────
// Rendered inside <ReactFlow> so useReactFlow() has access to the RF context.

function FitViewOnChange({ nodeCount }: { nodeCount: number }) {
  const { fitView } = useReactFlow()
  // Use a ref to skip the very first mount (fitView prop handles it) and only
  // refit on subsequent data changes (e.g., a new runId loads new nodes).
  const mounted = useRef(false)

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    if (nodeCount > 0) {
      const id = setTimeout(() => void fitView({ padding: 0.15, duration: 380 }), 80)
      return () => clearTimeout(id)
    }
  }, [fitView, nodeCount])

  return null
}

// ── MiniMap node colour ───────────────────────────────────────────────────────

function miniMapColor(node: Node): string {
  const data = node.data as SkillNodeData
  return (STATUS_CONFIG[data?.status]?.color) ?? DEPT_DEFAULT
}

// ── Inner canvas (rendered after data is ready) ───────────────────────────────

type InnerCanvasProps = {
  rfNodes: SkillNodeType[]
  rfEdges: RFEdge[]
  onNodeClick: (node: SkillNodeType) => void
}

function InnerCanvas({ rfNodes, rfEdges, onNodeClick }: InnerCanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<SkillNodeType>(rfNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<RFEdge>(rfEdges)

  // Sync external data (new runId fetch OR SSE patches) into React Flow state.
  useEffect(() => { setNodes(rfNodes) }, [rfNodes, setNodes])
  useEffect(() => { setEdges(rfEdges) }, [rfEdges, setEdges])

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      onNodeClick={(_, node) => onNodeClick(node as SkillNodeType)}
      fitView
      fitViewOptions={{ padding: 0.15 }}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable
      minZoom={0.12}
      maxZoom={2.5}
      deleteKeyCode={null}
    >
      <FitViewOnChange nodeCount={rfNodes.length} />

      {/* Subtle dot-grid background */}
      <Background
        variant={BackgroundVariant.Dots}
        color="rgba(148, 163, 184, 0.09)"
        gap={28}
        size={1.2}
      />

      {/* Zoom / pan controls (bottom-left) */}
      <Controls showInteractive={false} />

      {/* Overview minimap (bottom-right) */}
      <MiniMap
        nodeColor={miniMapColor}
        maskColor="rgba(6, 10, 16, 0.65)"
        nodeBorderRadius={8}
      />
    </ReactFlow>
  )
}

// ── Header bar ────────────────────────────────────────────────────────────────

function DAGHeader({
  dagMap,
  sseStatus,
}: {
  dagMap:     RunDAGMap
  sseStatus:  SSEConnectionStatus
}) {
  const running = dagMap.nodes.filter((n) => n.status === 'running').length
  const waiting = dagMap.nodes.filter((n) => n.status === 'waiting_for_approval').length
  const failed  = dagMap.nodes.filter((n) => n.status === 'failed').length
  const done    = dagMap.nodes.filter((n) => n.status === 'completed').length

  const runStatusCfg =
    dagMap.runStatus === 'running'   ? { color: '#3b82f6', label: 'Running'   } :
    dagMap.runStatus === 'waiting'   ? { color: '#f59e0b', label: 'Waiting'   } :
    dagMap.runStatus === 'succeeded' ? { color: '#22c55e', label: 'Succeeded' } :
    dagMap.runStatus === 'failed'    ? { color: '#ef4444', label: 'Failed'    } :
                                       { color: '#475569', label: dagMap.runStatus }

  const sseCfg = SSE_STATUS_CONFIG[sseStatus]

  return (
    <div className="flex items-center gap-[14px] px-[18px] pt-3 pb-[10px] border-b border-[rgba(148,163,184,0.1)] bg-[rgba(13,21,32,0.6)] flex-shrink-0">
      <div>
        <div className="text-[10px] font-[800] tracking-[0.16em] uppercase text-[#5d7490]">Logic Map</div>
        <div className="text-[13px] font-bold text-[#c8d9ee] font-mono">{dagMap.runId}</div>
      </div>

      <div className="flex-1" />

      <div className="flex gap-[14px]">
        {running > 0 && (
          <div className="flex items-center gap-[6px] text-[11px] text-[#6b849e]">
            <span className="font-bold text-[#a8bfd4] tabular-nums" style={{ color: '#3b82f6' }}>{running}</span>
            running
          </div>
        )}
        {waiting > 0 && (
          <div className="flex items-center gap-[6px] text-[11px] text-[#6b849e]">
            <span className="font-bold tabular-nums" style={{ color: '#f59e0b' }}>{waiting}</span>
            waiting
          </div>
        )}
        {failed > 0 && (
          <div className="flex items-center gap-[6px] text-[11px] text-[#6b849e]">
            <span className="font-bold tabular-nums" style={{ color: '#ef4444' }}>{failed}</span>
            failed
          </div>
        )}
        <div className="flex items-center gap-[6px] text-[11px] text-[#6b849e]">
          <span className="font-bold text-[#a8bfd4] tabular-nums">{done}/{dagMap.nodes.length}</span>
          complete
        </div>
      </div>

      {/* ── SSE connection indicator ── */}
      <span
        className="inline-flex items-center gap-[5px] px-2 py-0.5 pl-[6px] rounded-full text-[9.5px] font-[800] tracking-[0.12em] uppercase border border-transparent whitespace-nowrap"
        style={{
          color:       sseCfg.color,
          background:  `${sseCfg.color}12`,
          borderColor: `${sseCfg.color}28`,
        }}
        title={`SSE stream: ${sseStatus}`}
      >
        <span
          className={`w-[5px] h-[5px] rounded-full flex-shrink-0${sseCfg.pulse ? ' dag-header__sse-dot--pulse' : ''}`}
          style={{ background: sseCfg.color }}
        />
        {sseCfg.label}
      </span>

      {/* ── Run status badge ── */}
      <span
        className="inline-flex items-center gap-[6px] px-[10px] py-[3px] pl-[7px] rounded-full text-[10px] font-[800] tracking-[0.1em] uppercase"
        style={{
          color:      runStatusCfg.color,
          background: `${runStatusCfg.color}18`,
          border:     `1px solid ${runStatusCfg.color}33`,
        }}
      >
        <span
          style={{
            width:        5,
            height:       5,
            borderRadius: '50%',
            background:   runStatusCfg.color,
            display:      'inline-block',
          }}
        />
        {runStatusCfg.label}
      </span>
    </div>
  )
}

// ── Placeholder states ────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-[#4e6480]">
      <div className="dag-spinner" />
      <p className="text-[14px] font-bold text-[#6b849e] m-0">Loading graph…</p>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-[#4e6480]">
      <div className="text-[32px] opacity-40 leading-none">⚠</div>
      <p className="text-[14px] font-bold text-[#6b849e] m-0">Failed to load DAG</p>
      <p className="text-[12px] text-[#4e6480] m-0 max-w-[320px] text-center leading-[1.5]">{message}</p>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-[#4e6480]">
      <div className="text-[32px] opacity-40 leading-none">⬡</div>
      <p className="text-[14px] font-bold text-[#6b849e] m-0">No steps yet</p>
      <p className="text-[12px] text-[#4e6480] m-0 max-w-[320px] text-center leading-[1.5]">
        The run has not produced any execution steps. The graph will appear once
        the orchestrator begins processing.
      </p>
    </div>
  )
}

// ── DAGCanvas (main export) ───────────────────────────────────────────────────

export type DAGCanvasProps = {
  /** Platform run ID — used to call GET /v1/runs/{runId}/dag-map */
  runId: string
  /** Total height of the canvas (header bar + flow area). Default 580px. */
  height?: number
}

type LoadStatus = 'loading' | 'error' | 'ready'

// ── Toast ─────────────────────────────────────────────────────────────────────

type ToastKind  = 'success' | 'error'
type ToastEntry = { id: string; message: string; kind: ToastKind; exiting: boolean }

// ── DAGCanvas ─────────────────────────────────────────────────────────────────

export function DAGCanvas({ runId, height = 580 }: DAGCanvasProps) {
  const [dagMap,    setDagMap]    = useState<RunDAGMap | null>(null)
  const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading')
  const [errorMsg,  setErrorMsg]  = useState('')
  const [sseStatus, setSseStatus] = useState<SSEConnectionStatus>('idle')

  // ── Step Details sidebar state ────────────────────────────────────────────────
  // Tracks which non-approval node (if any) the user has clicked.
  // Mutually exclusive with selectedApprovalNodeId: opening one closes the other.
  const [selectedStepNodeId, setSelectedStepNodeId] = useState<string | null>(null)

  // ── Toast state ──────────────────────────────────────────────────────────────
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const addToast = useCallback((message: string, kind: ToastKind) => {
    const id = crypto.randomUUID()
    setToasts((prev) => [...prev, { id, message, kind, exiting: false }])
    // Trigger exit animation at 3 s, then remove from DOM at 3.18 s.
    setTimeout(() => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
      )
    }, 3_000)
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3_180)
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    )
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 180)
  }, [])

  // ── Approval node selection (via observability store) ────────────────────────
  const selectedApprovalNodeId = useObservabilityStore((s) => s.selectedApprovalNodeId)
  const setSelectedApprovalNode = useObservabilityStore((s) => s.setSelectedApprovalNode)
  const selectStep              = useObservabilityStore((s) => s.selectStep)

  /** The live GoDAGNode for the currently selected approval node, or null. */
  const selectedNode = useMemo(
    () => dagMap?.nodes.find((n) => n.id === selectedApprovalNodeId) ?? null,
    [dagMap, selectedApprovalNodeId],
  )

  /** The live GoDAGNode for the currently selected step-details node, or null. */
  const selectedStepNode = useMemo(
    () => dagMap?.nodes.find((n) => n.id === selectedStepNodeId) ?? null,
    [dagMap, selectedStepNodeId],
  )

  // ── Stable layout cache ───────────────────────────────────────────────────
  //
  // dagre layout is expensive-ish and positions don't change during a run
  // (only statuses do).  We cache computed positions in a ref keyed by the
  // NUL-joined node-ID list; dagre only re-runs when that key changes.
  const layoutPositions = useRef<Map<string, { x: number; y: number }>>(new Map())
  const topologyKeyRef  = useRef<string>('')

  // ── Initial DAG fetch ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!runId) return

    let cancelled = false
    setLoadStatus('loading')
    setErrorMsg('')
    setDagMap(null)
    setSseStatus('idle')
    setSelectedApprovalNode(null) // close any open sidebar when run changes
    setSelectedStepNodeId(null)

    // No caller-supplied headers here: apiFetch injects fresh X-Tenant-Id and
    // Authorization per request (and re-injects on its 401 refresh-retry) — a
    // render-time header snapshot would override that fresh auth.
    api
      .get<RunDAGMap>(`/v1/runs/${encodeURIComponent(runId)}/dag-map`)
      .then((data) => {
        if (cancelled) return
        setDagMap(data)
        setLoadStatus('ready')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setErrorMsg(apiErrorMessage(err, 'Unexpected error loading DAG'))
        setLoadStatus('error')
      })

    return () => { cancelled = true }
  }, [runId])

  // ── SSE stream ────────────────────────────────────────────────────────────
  //
  // Starts only once the initial DAG has loaded (loadStatus === 'ready') so
  // we never patch a null dagMap.
  //
  // Retry strategy: exponential back-off starting at 1 s, doubling each
  // attempt, capped at 30 s.  The back-off resets to 1 s on a successful
  // HTTP 200 handshake.
  //
  // requestAnimationFrame batching: incoming events push functional updaters
  // onto `pending[]`.  The first push schedules a rAF; subsequent pushes in
  // the same JS frame are free-riding.  On the rAF tick all accumulated
  // updaters are reduced into a single setDagMap call → one React render.

  useEffect(() => {
    if (!runId || loadStatus !== 'ready') return

    let alive = true
    // Exponential-backoff-with-jitter state.
    // attempt 0 → ~1 s, attempt 1 → ~2 s, …, capped at 30 s.
    let retryAttempt                                           = 0
    let retryTimerId: ReturnType<typeof setTimeout> | null     = null
    let abortCtrl:    AbortController | null                   = null
    let rafId:        number | null                            = null

    const pending: Array<(prev: RunDAGMap | null) => RunDAGMap | null> = []

    // ── rAF batch flush ────────────────────────────────────────────────────

    function flushPending() {
      rafId = null
      if (!alive || pending.length === 0) return
      const updates = pending.splice(0)
      setDagMap((prev) => updates.reduce((acc, fn) => fn(acc), prev))
    }

    /**
     * Queue an immutable dagMap updater.  The first call in a JS event-loop
     * tick schedules a rAF; subsequent calls just push onto the queue.
     */
    function scheduleUpdate(
      updater: (prev: RunDAGMap | null) => RunDAGMap | null,
    ) {
      pending.push(updater)
      if (rafId === null) {
        rafId = requestAnimationFrame(flushPending)
      }
    }

    // ── SSE event dispatcher ───────────────────────────────────────────────
    //
    // Go wraps every SSE data frame in a StreamEnvelope:
    //   { "type": "node.status_changed", "payload": { ...eventFields } }
    //
    // The mock server sends raw event objects without the wrapper.
    // We unwrap `payload` when present and fall back to the root object
    // so the handler is compatible with both backends.

    function handleEvent(eventType: string, rawData: string) {
      try {
        const parsed = JSON.parse(rawData) as Record<string, unknown>

        // Unwrap StreamEnvelope — if `payload` key exists, use it;
        // otherwise treat the whole parsed object as the event payload.
        const data = (parsed.payload !== undefined
          ? parsed.payload
          : parsed) as Record<string, unknown>

        switch (eventType) {
          // ------------------------------------------------------------------
          // dag.snapshot (Go initial hydration)
          //   Payload: full RunDAGMap  { runId, nodes, edges, runStatus, … }
          //   Action:  Replace the entire dagMap with the snapshot.
          //            This replaces the separate initial REST fetch that the
          //            mock uses — Go sends the full state as the first SSE
          //            event so clients never need to poll for initial state.
          // ------------------------------------------------------------------
          case 'dag.snapshot': {
            const snapshot = data as RunDAGMap
            scheduleUpdate(() => snapshot)
            break
          }

          // ------------------------------------------------------------------
          // node.status_changed
          //   Go payload:  { runId, nodeId, skillId, nodeStatus, runStatus,
          //                  errorCode?, occurredAt }
          //   Mock payload: { nodeId, nodeStatus }
          //   Action:  Patch the matching node's status (and optional error
          //            fields).  Triggers CSS class change on SkillNode
          //            (glow-blue for running, glow-amber for approval).
          // ------------------------------------------------------------------
          case 'node.status_changed': {
            const ev = data as NodeStatusChangedEvent
            scheduleUpdate((prev) =>
              prev
                ? {
                    ...prev,
                    nodes: prev.nodes.map((n) =>
                      n.id === ev.nodeId
                        ? {
                            ...n,
                            status:       ev.nodeStatus,
                            errorCode:    ev.errorCode,
                            errorMessage: ev.errorMessage,
                          }
                        : n
                    ),
                    // Go includes the updated runStatus in every node event.
                    ...(ev.runStatus ? { runStatus: ev.runStatus } : {}),
                  }
                : prev
            )
            break
          }

          // ------------------------------------------------------------------
          // approval.requested
          //   Go payload:  { nodeId, runId, gateId, occurredAt }
          //   Mock payload: { nodeId }
          //   Action:  Force the node to waiting_for_approval so the amber
          //            pulse animation kicks in immediately, before a
          //            node.status_changed echo may arrive.
          // ------------------------------------------------------------------
          case 'approval.requested': {
            const ev = data as ApprovalRequestedEvent
            scheduleUpdate((prev) =>
              prev
                ? {
                    ...prev,
                    nodes: prev.nodes.map((n) =>
                      n.id === ev.nodeId
                        ? { ...n, status: 'waiting_for_approval' as const }
                        : n
                    ),
                  }
                : prev
            )
            break
          }

          // ------------------------------------------------------------------
          // run.status_changed
          //   Go payload:  { runId, runStatus, occurredAt }
          //   Mock payload: { runStatus }
          //   Action:  Update the run-level status badge in the header.
          // ------------------------------------------------------------------
          case 'run.status_changed': {
            const ev = data as RunStatusChangedEvent
            scheduleUpdate((prev) =>
              prev ? { ...prev, runStatus: ev.runStatus } : prev
            )
            break
          }

          // Unknown event types are silently ignored so forward-compatibility
          // is maintained without crashing the canvas.
        }
      } catch {
        // Malformed JSON payload — discard silently.
      }
    }

    // ── Connect / reconnect ────────────────────────────────────────────────

    async function connect() {
      if (!alive) return
      abortCtrl = new AbortController()
      setSseStatus('connecting')

      try {
        const res = await fetch(
          `/v1/runs/${encodeURIComponent(runId)}/events`,
          {
            headers: {
              // api.headers() injects X-Tenant-Id and Authorization.
              // EventSource cannot do this — another reason to use fetch.
              ...(api.headers() as Record<string, string>),
              Accept:          'text/event-stream',
              'Cache-Control': 'no-cache',
              // Distributed tracing — correlates this stream through the Go
              // HTTP layer and into NATS worker logs.
              'X-Trace-Id':    crypto.randomUUID(),
            },
            signal: abortCtrl.signal,
          }
        )

        if (!res.ok || !res.body) {
          throw new Error(`SSE handshake failed: HTTP ${res.status} ${res.statusText}`)
        }

        // Successful HTTP handshake → reset back-off counter, mark stream live.
        retryAttempt = 0
        if (alive) setSseStatus('live')

        // parseSSEStream resolves when the server closes the connection.
        await parseSSEStream(res.body, handleEvent)

        // Server closed the stream cleanly (e.g. run finished).  Schedule
        // a reconnect in case it was a transient close.
        if (alive) {
          setSseStatus('closed')
          scheduleRetry()
        }
      } catch (err) {
        if (!alive) return
        // AbortError is raised by our own cleanup → do not retry.
        if (err instanceof DOMException && err.name === 'AbortError') return
        if (alive) {
          setSseStatus('error')
          scheduleRetry()
        }
      }
    }

    function scheduleRetry() {
      if (!alive) return
      // Full-jitter exponential back-off.
      //   exp   = min(BASE * 2^attempt, CAP)
      //   delay = exp ± 25 % uniform jitter  → prevents thundering-herd on
      //           mass reconnects (e.g. server restart with many open tabs).
      const BASE_MS = 1_000
      const CAP_MS  = 30_000
      const exp     = Math.min(BASE_MS * Math.pow(2, retryAttempt), CAP_MS)
      const jitter  = exp * 0.25 * (Math.random() * 2 - 1) // ±25 %
      const delay   = Math.max(200, Math.round(exp + jitter))
      retryAttempt++
      retryTimerId  = setTimeout(() => void connect(), delay)
    }

    void connect()

    // ── Cleanup ───────────────────────────────────────────────────────────
    return () => {
      alive = false
      if (retryTimerId !== null) clearTimeout(retryTimerId)
      if (rafId        !== null) cancelAnimationFrame(rafId)
      abortCtrl?.abort()
    }
  }, [runId, loadStatus]) // re-run when runId changes or initial load completes

  // ── Auto-close ReviewSidebar on SSE-driven status transition ──────────────
  //
  // When the SSE stream patches dagMap and moves the selected node away from
  // `waiting_for_approval` (approval accepted → running/completed, or rejected
  // → pending), close the sidebar and surface a contextual toast.
  useEffect(() => {
    if (!selectedApprovalNodeId || !dagMap) return
    const node = dagMap.nodes.find((n) => n.id === selectedApprovalNodeId)
    if (!node || node.status === 'waiting_for_approval') return

    // Status has changed — decision was processed by the backend.
    setSelectedApprovalNode(null)

    if (node.status === 'running' || node.status === 'completed') {
      addToast(`"${node.label}" — approved, now ${node.status}`, 'success')
    } else if (node.status === 'failed') {
      addToast(`"${node.label}" — step failed`, 'error')
    }
    // pending / other statuses: close silently
  }, [dagMap, selectedApprovalNodeId, setSelectedApprovalNode, addToast])

  // ── Node click handler ────────────────────────────────────────────────────
  //
  // Wrapped in useCallback so InnerCanvas's prop reference is stable between
  // renders that don't change the handler's dependencies.
  //
  // Routing logic:
  //   waiting_for_approval → ReviewSidebar  (approval decision flow)
  //   all other statuses   → StepDetailsSidebar  (input/output inspector)
  //
  // The two panels are mutually exclusive: opening one always closes the other.
  const handleNodeClick = useCallback(
    (node: SkillNodeType) => {
      // Always update the AuditLogView step filter to the clicked node.
      selectStep(node.id)

      if (node.data.status === 'waiting_for_approval') {
        // Open the approval panel; close the step-details panel.
        setSelectedApprovalNode(node.id)
        setSelectedStepNodeId(null)
      } else {
        // Open the step-details panel; close the approval panel.
        setSelectedApprovalNode(null)
        setSelectedStepNodeId((prev) => (prev === node.id ? null : node.id))
      }
    },
    [selectStep, setSelectedApprovalNode],
  )

  // ── Derive React Flow nodes and edges ─────────────────────────────────────
  //
  // dagre is only invoked when the set of node IDs changes (topology change).
  // Pure status patches skip dagre entirely — just rebuild node objects with
  // the cached positions.
  const { rfNodes, rfEdges } = useMemo(() => {
    if (!dagMap) return { rfNodes: [] as SkillNodeType[], rfEdges: [] as RFEdge[] }

    // Build a stable key from the sorted node-ID list.
    const topologyKey = dagMap.nodes.map((n) => n.id).join('\x00')

    if (topologyKey !== topologyKeyRef.current) {
      // Node topology changed (initial load, or dynamic node addition).
      // Re-run dagre and cache the resulting positions.
      topologyKeyRef.current = topologyKey

      const stubNodes: SkillNodeType[] = dagMap.nodes.map((n) => ({
        id:       n.id,
        type:     'skill' as const,
        position: { x: 0, y: 0 },
        width:    NODE_W,
        height:   NODE_H,
        draggable: false,
        data:     buildNodeData(n),
      }))

      const stubEdges: RFEdge[] = dagMap.edges.map((e, i) => ({
        id:     `layout-${i}`,
        source: e.sourceNodeId,
        target: e.targetNodeId,
      }))

      const { nodes: laid } = applyDagreLayout(stubNodes, stubEdges)
      for (const n of laid) {
        layoutPositions.current.set(n.id, n.position)
      }
    }

    // Build full node objects using the cached positions.
    const rfNodes: SkillNodeType[] = dagMap.nodes.map((n) => ({
      id:       n.id,
      type:     'skill' as const,
      position: layoutPositions.current.get(n.id) ?? { x: 0, y: 0 },
      width:    NODE_W,
      height:   NODE_H,
      draggable: false,
      data:     buildNodeData(n),
    }))

    // Edges re-derive on every dagMap change so animated/colour state tracks
    // the current running node even after SSE patches.
    const rfEdges = buildRFEdges(dagMap)

    return { rfNodes, rfEdges }
  }, [dagMap])

  // ── Render ────────────────────────────────────────────────────────────────

  const flowHeight = height - 53 // subtract header bar height (~53 px)

  return (
    <div
      className="flex flex-col w-full rounded-[var(--radius-lg,16px)] overflow-hidden border border-[rgba(148,163,184,0.13)] bg-gradient-to-b from-[rgba(10,16,24,0.97)] to-[rgba(6,10,16,0.99)] relative"
      style={{ height }}
    >
      {/* ── Header: always rendered ── */}
      {dagMap ? (
        <DAGHeader dagMap={dagMap} sseStatus={sseStatus} />
      ) : (
        <div className="flex items-center gap-[14px] px-[18px] pt-3 pb-[10px] border-b border-[rgba(148,163,184,0.1)] bg-[rgba(13,21,32,0.6)] flex-shrink-0">
          <div>
            <div className="text-[10px] font-[800] tracking-[0.16em] uppercase text-[#5d7490]">Logic Map</div>
            <div className="text-[13px] font-bold font-mono" style={{ color: '#4e6480' }}>
              {loadStatus === 'loading' ? 'Fetching graph…' : 'Error loading graph'}
            </div>
          </div>
        </div>
      )}

      {/* ── Flow area ── */}
      <div className="flex-1 min-h-0" style={{ height: flowHeight }}>
        {loadStatus === 'loading' && <LoadingState />}
        {loadStatus === 'error'   && <ErrorState message={errorMsg} />}
        {loadStatus === 'ready'   && rfNodes.length === 0 && <EmptyState />}
        {loadStatus === 'ready'   && rfNodes.length > 0   && (
          <InnerCanvas
            rfNodes={rfNodes}
            rfEdges={rfEdges}
            onNodeClick={handleNodeClick}
          />
        )}
      </div>

      {/* ── Toast stack (bottom-left, above RF controls) ── */}
      {toasts.length > 0 && (
        <div className="dag-toast-stack" role="region" aria-live="polite" aria-label="Notifications">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`dag-toast dag-toast--${t.kind}${t.exiting ? ' dag-toast--exit' : ''}`}
              role="status"
            >
              <span className="dag-toast__icon" aria-hidden="true">
                {t.kind === 'success' ? '✓' : '⚠'}
              </span>
              {t.message}
              <button
                className="dag-toast__close"
                onClick={() => dismissToast(t.id)}
                aria-label="Dismiss notification"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Review sidebar (approval gates) ── */}
      <ReviewSidebar
        node={selectedNode}
        runId={runId}
        onClose={() => setSelectedApprovalNode(null)}
        onToast={addToast}
      />

      {/* ── Step Details sidebar (all other nodes) ── */}
      <StepDetailsSidebar
        node={selectedStepNode}
        runId={runId}
        onClose={() => setSelectedStepNodeId(null)}
      />
    </div>
  )
}
